import { useEffect, useRef, useState, type UIEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from '@vnetwork/ui/primitives';
import { COMPANY_NAME, TERMS_URL, fetchTerms } from './terms-content';
import styles from './TermsDialog.module.css';

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user reaches the end and confirms agreement. */
  onAgree: () => void;
}

const BOTTOM_THRESHOLD = 24; // px slack so the last line counts as "the end".

/**
 * Terms of Service reader. The text is fetched live from the company website.
 * The "I Agree" action stays disabled until the document has loaded and the
 * user has scrolled to the bottom of it.
 */
export function TermsDialog({ open, onOpenChange, onAgree }: TermsDialogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [reachedEnd, setReachedEnd] = useState(false);

  const terms = useQuery({
    queryKey: ['terms-of-service', TERMS_URL],
    queryFn: fetchTerms,
    enabled: open,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  // Reset on close; once the text is in, unlock immediately if it is too short to scroll.
  useEffect(() => {
    if (!open) {
      setReachedEnd(false);
      return;
    }
    const el = scrollRef.current;
    if (terms.data && el && el.scrollHeight <= el.clientHeight + BOTTOM_THRESHOLD) {
      setReachedEnd(true);
    }
  }, [open, terms.data]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (terms.data && el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_THRESHOLD) {
      setReachedEnd(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Terms of Service</DialogTitle>
          <DialogDescription>
            {COMPANY_NAME}
            {terms.data?.updated ? ` · Updated ${terms.data.updated}` : ''}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div ref={scrollRef} onScroll={handleScroll} className={styles.scroll}>
            {terms.isPending ? (
              <p className={styles.status}>Loading the Terms of Service…</p>
            ) : terms.isError ? (
              <div className={styles.status} role="alert">
                <p>The Terms of Service could not be loaded.</p>
                <p>
                  <Button variant="secondary" onClick={() => void terms.refetch()}>
                    Try again
                  </Button>{' '}
                  <a href={TERMS_URL} target="_blank" rel="noreferrer noopener">
                    Open the Terms in a new tab
                  </a>
                </p>
              </div>
            ) : (
              <>
                <div className={styles.prose} dangerouslySetInnerHTML={{ __html: terms.data.html }} />
                <p className={styles.end}>— End of Terms —</p>
              </>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          {!reachedEnd && terms.data ? (
            <span className={styles.hint}>Scroll to the bottom to continue</span>
          ) : null}
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button className={styles.agree} disabled={!reachedEnd} onClick={onAgree}>
            I Agree
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
